import React, { useEffect, useState } from 'react';
import { messages } from './Messages';
import Select from '../../../../components/Select';
import { IOption } from '../../../../components/Select/Select';
import HoverIcon from '../../../../components/HoverIcon/HoverIcon';
import cloneDeep from 'lodash/cloneDeep';
import Button from '../../../../components/Button/Button';
import { smartDateString } from 'utils/date';
import {
  ErrorWrapper,
  PersonTopicList,
  TermEditorWrapper,
  TopicItem,
  TopicWrapper,
} from './style';
import notify from '../../../../components/Notify';
import Icon from '../../../../components/Icon/Icon';
import IconTypes from '../../../../components/Icon/IconTypes';
import {
  black,
  blue,
  gray,
  green,
  lightBlue,
  lightBlueSolid,
  lightGray,
  lightGreenSolid,
  lightRed,
  lightRedSolid,
  lightYellow,
  lightYellowSolid,
  orange,
  red,
  stringToColour,
} from '../../../../utils/style/colors';
import { PDPProblems } from './PDPPersonManager';
import Collapsible from '../../../../components/Collapsible/Collapsible';
import Badge from '../../../../components/Badge/Badge';
import APopover from '../../../../components/APopover';
import Modal from '../../../../components/Modal';
import Input from '../../../../components/Input';
import {
  isANumber,
  convertDigitToProperFormat,
} from '../../../../utils/number';
import { withTitle } from '../../../../components/withTitle';
import { notifyError } from '../../../../components/Notify/Notify';
import { deepEqual } from '../../../../utils/happyFunctions';
import { Beforeunload } from 'react-beforeunload';
import {
  ApprovedStateByUser,
  CompanyPDPState,
  PDPProgram,
  PersonalTermTopic,
  Phase,
  Term,
  Topic,
} from '../../../../store/pdp/types';
import { ITags } from '../../../../store/providers/providerTypes';
import { IThisUser, IUsers } from '../../../../store/store';
import { ICompany } from '../../../../store/companies/companiesTypes';
import { createOrUpdateTopicFn } from '../../../../store/pdp/pdpSlice';
import { MoreSpan } from '../../../Profile/Panels/DocumentManager/styles';
import Dropdown from '../../../../components/Dropdown';
import UserMixpanel from 'utils/mixpanel/MixpanelClass';

const { Menu, MenuItem } = Dropdown;

interface ComponentProps {
  term: Term;
  phase?: Phase;
  addNewTopic: (topic: Topic) => ReturnType<typeof createOrUpdateTopicFn>;
  program?: PDPProgram;
  new?: boolean;
  selectTagOptions: IOption[];
  createOrUpdatePDPProgram: (topic: PDPProgram) => Promise<PDPProgram>;
  deletePDPProgram?: (program: PDPProgram) => Promise<any>;
  allTags: ITags['data'];
  availableUsersToAdd: number[];
  users: IUsers['map'];
  pdpId: number;
  thisUser: IThisUser;
  pdpState: CompanyPDPState;
  editable: boolean;
  company: ICompany;
  getErrors: (it: PDPProgram) => PDPProblems[];
  close?: (ignoreChanges?: boolean) => void;
  inReportView?: boolean;
  simple?: boolean;
  isPDPAdmin?: boolean;
  setDirty?: (dirty: boolean) => any;
}

const typeColors = {
  New: lightBlue,
  Unknown: lightYellow,
  Approved: lightGreenSolid,
  Rejected: lightRed,
};

function getIconType(asbu: ApprovedStateByUser, index: number): IconTypes {
  if (index === 0 && asbu.approved === 'Approved') return 'user-plus';
  if (asbu.approved === 'Approved') return 'user-check';
  return 'user-x';
}

function getIconColor(asbu: ApprovedStateByUser, index: number): string {
  if (index === 0) return blue;
  if (asbu.approved === 'Approved') return green;
  return red;
}

export function AddModal(props: {
  onClose: () => void;
  active: boolean;
  value: string;
  onChange: (e: any) => void;
  onAdd: () => void;
  loading: boolean;
}) {
  return (
    <Modal
      title={messages.requestNewTopic}
      width={'300px'}
      height={'50%'}
      onClose={props.onClose}
      isActive={props.active}
    >
      <div>
        <p>{messages.pleaseEnterTopicName}</p>
        <Input
          id="requestTopicTextId"
          type="text"
          value={props.value}
          onChange={props.onChange}
        />
        <Button
          color={'green'}
          onClick={() => {
            const result = confirm(messages.areYouSure);
            if (result) props.onAdd();
          }}
          loading={props.loading}
          disabled={props.value.trim().length === 0}
        >
          {messages.requestNewTopic}
        </Button>
      </div>
    </Modal>
  );
}

export const PDPPersonEditor: React.FC<ComponentProps> = (props) => {
  const newProgram = {
    properties: {
      topics: [] as PersonalTermTopic[],
    },
    termId: props.term.id!,
    memberUserId: 0,
    id: 0,
  } as PDPProgram;
  const [editing, setEditing] = useState(props.new || false);
  const [requestingNewTopicModalOpen, setRequestingNewTopicModalOpen] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState(newProgram);
  const mandatorPrograms = editingProgram.memberUserId
    ? Object.values(props.pdpState.topics || {}).filter((it: Topic) =>
        it.properties.mandatoryForRoles.some(
          (role) =>
            !!props.company.members[editingProgram.memberUserId].roles[role]
        )
      )
    : [];
  useEffect(() => {
    setEditingProgram(
      props.new
        ? newProgram
        : ((cloneDeep(props.program) as PDPProgram) ?? newProgram)
    );
    setEditing(props.editable);
  }, [props.program, props.new]);

  const oldTopicMap: {
    [key: number]: 'Approved' | 'Rejected' | 'Unknown';
  } = {};
  const program = editing ? editingProgram! : props.program || newProgram;
  program?.properties.topics.forEach((it) => {
    oldTopicMap[it.topicId] =
      it.approvedStateByUsers[it.approvedStateByUsers.length - 1]?.approved ||
      'Unknown';
  });

  const allowApprove =
    editing &&
    (props.phase?.validActions.length === 0 ||
      props.phase?.validActions.includes('Approve'));
  const allowAdd =
    editing &&
    (props.phase?.validActions.length === 0 ||
      props.phase?.validActions.includes('Add') ||
      !props.phase); /*pdp admin*/
  const currentTopicMap: {
    [key: number]: 'Approved' | 'Rejected' | 'Unknown' | 'New';
  } = {};
  const topicIndex: {
    [key: number]: number;
  } = {};
  program?.properties.topics.forEach((it, index) => {
    topicIndex[it.topicId] = index;
    if (it.addedInPhase === props.phase?.id) {
      currentTopicMap[it.topicId] = 'New';
      return;
    }
    const lastState =
      it.approvedStateByUsers[it.approvedStateByUsers.length - 1];
    if (
      !lastState ||
      (lastState.phaseId !== props.phase?.id &&
        lastState.approved !== 'Rejected')
    ) {
      const isMandatory = mandatorPrograms.find((p) => p.id === it.topicId);
      currentTopicMap[it.topicId] =
        allowApprove && !isMandatory ? 'Unknown' : 'Approved';
    } else {
      currentTopicMap[it.topicId] = lastState.approved;
    }
  });

  const userOptions = props.availableUsersToAdd.map((it) => {
    // @ts-expect-error what?!
    return { value: it, label: props.users.name || '' };
  });
  const [problems, setProblems] = useState([] as PDPProblems[]);
  useEffect(() => {
    setProblems(props.getErrors(program));
  }, [program]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [toAddTopic, setToAddTopic] = useState(
    undefined as PersonalTermTopic | undefined
  );
  function approveTopic(
    cloneIn: PDPProgram | null,
    ...personTopics: PersonalTermTopic[]
  ) {
    const clone = cloneIn || (cloneDeep(editingProgram) as PDPProgram);
    personTopics.forEach((personTopic) => {
      const target = clone.properties.topics.find(
        (it) => it.topicId === personTopic.topicId
      );
      const current = target!.approvedStateByUsers.findIndex((it) => {
        return it.phaseId === props.phase!.id;
      });
      if (current >= 0) {
        target!.approvedStateByUsers[current].approved = 'Approved';
        target!.approvedStateByUsers[current].userId = Number(
          props.thisUser.userId
        );
      } else {
        target!.approvedStateByUsers.push({
          userId: Number(props.thisUser.userId),
          approved: 'Approved',
          phaseId: props.phase!.id,
          tags:
            target!.approvedStateByUsers.length === 0
              ? []
              : target!.approvedStateByUsers[
                  target!.approvedStateByUsers.length - 1
                ].tags,
        });
      }
    });
    setEditingProgram(clone);
  }
  function setLastApproveTag(
    personTopic: PersonalTermTopic,
    titleTagId: number,
    valueTagId: number | undefined
  ) {
    const clone = cloneDeep(editingProgram) as PDPProgram;
    let target = clone.properties.topics.find(
      (it) => it.topicId === personTopic.topicId
    );
    let setCandid = false;
    if (!target) {
      target = personTopic;
      setCandid = true;
    }
    const current = target!.approvedStateByUsers.find((it) => {
      return it.phaseId === props.phase?.id;
    })!;
    current.tags = [
      ...(valueTagId ? [valueTagId] : []),
      ...current.tags.filter((it) => props.allTags[it].parentId !== titleTagId),
    ];
    if (!setCandid) {
      setEditingProgram(clone);
    } else {
      setToAddTopic({ ...target });
    }
  }

  function rejectTopic(personTopic: PersonalTermTopic) {
    const clone = cloneDeep(editingProgram) as PDPProgram;
    const target = clone.properties.topics.find(
      (it) => it.topicId === personTopic.topicId
    );
    const current = target!.approvedStateByUsers.findIndex((it) => {
      return it.phaseId === props.phase!.id;
    });
    if (current >= 0) {
      target!.approvedStateByUsers[current].approved = 'Rejected';
      target!.approvedStateByUsers[current].userId = Number(
        props.thisUser.userId
      );
    } else {
      target!.approvedStateByUsers.push({
        userId: Number(props.thisUser.userId),
        approved: 'Rejected',
        phaseId: props.phase!.id,
        tags:
          target!.approvedStateByUsers.length === 0
            ? []
            : target!.approvedStateByUsers[
                target!.approvedStateByUsers.length - 1
              ].tags,
      });
    }
    setEditingProgram(clone);
  }
  const [requestingTopic, setRequestingTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const dirty =
    editingProgram.id === props.program?.id &&
    !deepEqual(editingProgram, props.program);
  useEffect(() => {
    props.setDirty?.(dirty);
  }, [dirty]);
  function requestTopic(name: string) {
    setRequestingTopic(true);
    name = name + '*';
    props
      .addNewTopic({
        properties: {
          name,
          tags: [],
          relatedToRoles: [],
          mandatoryForRoles: [],
        },
        companyPDPId: props.pdpId,
        status: 'Proposed',
      })
      .then(({ topic }) => {
        setCandidTopic(topic.id!);
      })
      .finally(() => {
        setRequestingTopic(false);
        setRequestingNewTopicModalOpen(false);
      });
  }

  const [openDetails, setOpenDetails] = useState(!props.simple);
  const [searchTopicText, setSearchTopicText] = useState('');
  function addTopic(topicIds: number[]) {
    const clone = cloneDeep(editingProgram) as PDPProgram;
    topicIds.forEach((topicId) => {
      clone.properties.topics.push({
        topicId: topicId,
        approvedStateByUsers: [
          {
            userId: Number(props.thisUser.userId)!,
            phaseId: props.phase?.id as any,
            approved: 'Approved',
            tags: [],
          },
        ],
        addedByUser: Number(props.thisUser.userId),
        addedInPhase: props.phase?.id as any,
      });
    });
    setEditingProgram(clone);
    return clone;
  }
  function setCandidTopic(topicId: number) {
    setToAddTopic({
      topicId: topicId,
      approvedStateByUsers: [
        {
          userId: Number(props.thisUser.userId)!,
          phaseId: props.phase?.id as any,
          approved: 'Approved',
          tags: [],
        },
      ],
      addedByUser: Number(props.thisUser.userId),
      addedInPhase: props.phase?.id as any,
    });
  }
  function addCondidTopic() {
    const clone = cloneDeep(editingProgram) as PDPProgram;
    if (toAddTopic) clone.properties.topics.push(toAddTopic);
    setEditingProgram(clone);
    setToAddTopic(undefined);
    notify(messages.topicAddedSelectNextTopic);
    return clone;
  }

  const [limitsOpen, setLimitsOpen] = useState(true);
  const limits = Object.entries(props.term.properties.perTagLimits)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([tagId, limit]) => {
      function getError() {
        if (tagId === '-1') {
          if (limit.min && (limit.max ?? 100000) < 10000)
            return messages.pleaseSelectAtLeastAndAtMost(limit.min, limit.max!);
          if (limit.min) return messages.pleaseSelectAtLeast(limit.min);
          else if ((limit.max ?? 100000) < 10000)
            return messages.pleaseSelectAtMost(limit.max!);
          return null;
        }
        const parentId = props.allTags[Number(tagId)]?.parentId;
        const tagName = props.allTags[tagId].text;
        const parentName = props.allTags[parentId || 0]?.text;
        if (limit.min === -1) {
          return messages.pleaseSelectAllTopicsWith(parentName, tagName);
        }
        if ((limit.min || 0) > 0 && (limit.max || 100000) < 1000) {
          return messages.boundTopicOfTypeSelected(
            parentName,
            tagName,
            limit.min!,
            limit.max!
          );
        } else if ((limit.min || 0) > 0) {
          return messages.fewTopicOfTypeSelected(
            parentName,
            tagName,
            limit.min!
          );
        } else {
          return messages.extraTopicOfTypeSelected(
            parentName,
            tagName,
            limit.max!
          );
        }
      }

      return { message: getError(), tagId: Number(tagId) };
    })
    .filter((it) => !!it.message);

  const sendAssessmentTrack = () => {
    const mx = UserMixpanel.getInstance(Number(props.thisUser.userId));
    mx.handleAddAssessmentCounter(
      props.program?.memberUserId === Number(props.thisUser.userId)
    );
  };

  const getTopicCard = (
    topicId: number,
    status: 'Approved' | 'Rejected' | 'Unknown' | 'New' | 'ToAdd'
  ) => {
    const personTopic = program.properties.topics.find(
      (it) => it.topicId === topicId
    );
    const hasError = problems.find(
      (it) => it.level === 'error' && it.topics?.includes(topicId)
    );
    const hasWarn =
      !hasError &&
      problems.find(
        (it) => it.level === 'warning' && it.topics?.includes(topicId)
      );
    const topicProblems = problems.filter((it) => it.topics?.includes(topicId));
    const missingTagOptionKeys = new Set<number>(
      props.company.pdpPanels.find(
        (el) => el.id === props.pdpId
      )!.properties.topicTagsInPDP
    );
    const tagsName = {};
    props.pdpState.topics?.[topicId]?.properties?.tags?.forEach((tagId) => {
      const parentId = props.allTags[tagId]?.parentId;
      if (parentId !== null) {
        tagsName[props.allTags[parentId!].text] = props.allTags[tagId].text;
        missingTagOptionKeys.delete(parentId!);
      }
    });
    const color =
      status === 'Approved'
        ? lightGreenSolid
        : status === 'Rejected'
          ? lightRedSolid
          : status === 'New'
            ? lightBlueSolid
            : status === 'ToAdd'
              ? lightBlueSolid
              : lightYellowSolid;
    const isMandatory = mandatorPrograms.find((it) => it.id === topicId);
    return (
      <Collapsible
        key={topicId}
        headerWrapperPadding={'0'}
        headerBackgroundColor={color}
        customHeaderStyle={{ flex: 1 }}
        isOpen={openDetails}
        hideChevron
        onClose={() => {
          if (props.simple) setOpenDetails(false);
        }}
        onOpen={() => {
          if (props.simple) setOpenDetails(true);
        }}
        nested={true}
        noZ={true}
        style={{ overflow: 'visible' }}
        showOverflow={true}
        header={
          <TopicWrapper color={color}>
            <span style={{ display: 'flex' }}>
              <span
                style={{
                  display: 'flex',
                  // width: '200px',
                  // height: 'auto',
                  // overflowY: 'hidden',
                  // overflowX: 'auto',
                  color: black,
                }}
              >
                {(hasError || hasWarn) && !props.simple && (
                  <Icon
                    type={hasError ? 'alert-triangle' : 'alert-octagon'}
                    style={{ marginLeft: '5px' }}
                    color={hasError ? red : orange}
                    titlePlacement={'bottom'}
                    title={
                      <div>
                        {topicProblems.map((p, index) => {
                          return (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                              }}
                            >
                              <Icon
                                type={
                                  p.level === 'error'
                                    ? 'alert-triangle'
                                    : 'alert-octagon'
                                }
                                color={p.level === 'error' ? red : orange}
                              />
                              <div>{p.message}</div>
                            </div>
                          );
                        })}
                      </div>
                    }
                  />
                )}
                <span>
                  {props.pdpState.topics?.[topicId]?.properties?.name}
                </span>
              </span>
              <span style={{ display: 'flex' }}></span>
            </span>
            {!editing ? (
              <div />
            ) : isMandatory && !props.isPDPAdmin ? (
              <span style={{ color: red }}>*{messages.mandatory}</span>
            ) : (
              <span style={{ display: 'flex' }}>
                {!!isMandatory && (
                  <span
                    style={{
                      color: red,
                      margin: '0 10px',
                    }}
                  >
                    *{messages.mandatory}
                  </span>
                )}
                {allowAdd && status === 'New' && (
                  <HoverIcon
                    type={'trash'}
                    color={red}
                    onClick={() => {
                      const clone = cloneDeep(editingProgram) as PDPProgram;
                      clone.properties.topics = clone.properties.topics.filter(
                        (it) => it.topicId !== topicId
                      );
                      setEditingProgram(clone);
                    }}
                  />
                )}
                {personTopic && (
                  <>
                    {allowApprove && status === 'Unknown' && (
                      <>
                        <HoverIcon
                          type={'check-circle'}
                          color={green}
                          onClick={() => {
                            approveTopic(null, personTopic);
                          }}
                          title={messages.approve}
                        />
                        <HoverIcon
                          type={'x-circle'}
                          color={red}
                          onClick={() => {
                            rejectTopic(personTopic);
                          }}
                          title={messages.reject}
                        />
                      </>
                    )}
                    {allowApprove && status === 'Rejected' && (
                      <HoverIcon
                        type={'check-circle'}
                        color={green}
                        onClick={() => {
                          approveTopic(null, personTopic);
                        }}
                        title={messages.approve}
                      />
                    )}
                    {allowApprove && status === 'Approved' && (
                      <HoverIcon
                        type={'x-circle'}
                        color={red}
                        onClick={() => {
                          rejectTopic(personTopic);
                        }}
                        title={messages.reject}
                      />
                    )}
                  </>
                )}{' '}
              </span>
            )}
          </TopicWrapper>
        }
      >
        <div>
          {props.pdpState.topics?.[topicId]?.status === 'Proposed' && (
            <div style={{ color: orange }}>
              {messages.propesedWarning}
              <Icon
                color={blue}
                type={'info'}
                style={{ margin: '0 5px' }}
                title={messages.propesedWarningExtra}
              />
            </div>
          )}
          {!props.simple && (
            <>
              {(
                props.phase?.validTagsToSelect ||
                (props.isPDPAdmin
                  ? props.company.pdpPanels.find((el) => el.id === props.pdpId)!
                      .properties.topicTagsInPDP
                  : [])
              ) // Array.from(missingTagOptionKeys)
                .map((it) => props.allTags[it])
                .sort(
                  (a, b) =>
                    (a.order || Number(a.id)) - (b.order || Number(b.id))
                )
                .map((tag) => {
                  const currentValue: number | undefined = (
                    personTopic || toAddTopic
                  )?.approvedStateByUsers.reduce(
                    (current, item) => {
                      const newValue = item.tags.find((it) => {
                        return props.allTags[it]?.parentId === tag.id;
                      });
                      return newValue || current;
                    },
                    undefined as number | undefined
                  );
                  const editable =
                    status === 'ToAdd' ||
                    (personTopic!.approvedStateByUsers.length > 0 &&
                      personTopic?.approvedStateByUsers[
                        personTopic?.approvedStateByUsers.length - 1
                      ].phaseId === props.phase?.id);
                  return (
                    <div
                      key={tag.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Select
                        values={
                          currentValue
                            ? [
                                {
                                  label: props.allTags[currentValue].text,
                                  value: currentValue,
                                },
                              ]
                            : []
                        }
                        label={tag.text}
                        round={true}
                        inputStyle={{
                          height: '30px',
                        }}
                        containerStyle={{
                          width: '100%',
                        }}
                        options={Object.values(props.allTags)
                          .filter((it) => it.parentId === tag.id && !it.deleted)
                          .map((it) => ({
                            label: it.text,
                            value: it.id,
                          }))}
                        disabled={!editable || !editing}
                        onChangeValues={(values) => {
                          setLastApproveTag(
                            personTopic || toAddTopic!,
                            Number(tag.id),
                            Number(values[0]?.value) || undefined
                          );
                        }}
                      />
                    </div>
                  );
                })}
              {Object.entries(tagsName)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => {
                  return (
                    <div key={key}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <li>{key}</li>
                        <div>{value as string}</div>
                      </div>
                    </div>
                  );
                })}
            </>
          )}
          {personTopic?.approvedStateByUsers.map((asbu, index) => {
            return (
              <div key={asbu.userId}>
                <APopover
                  arrow
                  curveBorder={true}
                  target={
                    <div style={{ display: 'inline' }}>
                      <Icon
                        type={getIconType(asbu, index)}
                        color={getIconColor(asbu, index)}
                      />
                      <span style={{ margin: '0 10px' }}>
                        {props.users[asbu.userId].name}
                      </span>
                    </div>
                  }
                  placement={'top'}
                  trigger={'hover'}
                >
                  <div style={{ textWrap: 'wrap', maxWidth: '300px' }}>
                    {messages.actionByUserInPhase(
                      messages[index === 0 ? 'Request' : asbu.approved],
                      props.term.properties.phaseConfig.phases.find(
                        (it) => it.id === asbu.phaseId
                      )?.name || '-',
                      props.users[asbu.userId].name
                    )}
                  </div>
                </APopover>
              </div>
            );
          })}
        </div>
      </Collapsible>
    );
  };
  const ret = (
    <>
      <TermEditorWrapper
        style={{
          position: 'relative',
          minWidth: '300px',
          height: '100%',
          margin: '0px',
        }}
      >
        {dirty && <Beforeunload onBeforeunload={() => ' '} />}
        {!editing && props.close && (
          <HoverIcon
            style={{
              position: 'absolute',
              left: '5px',
              top: '5px',
            }}
            color={black}
            type={'x'}
            onClick={() => {
              props.close!();
            }}
          />
        )}
        <div
          style={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {props.inReportView ? (
              <div></div>
            ) : props.new ? (
              <Select
                containerStyle={{
                  marginTop: '20px',
                }}
                onChangeValues={(values) => {
                  const clone = cloneDeep(editingProgram) as PDPProgram;
                  clone.memberUserId = Number(values[0]?.value);
                  const mandatorPrograms = Object.values(
                    props.pdpState.topics || {}
                  ).filter((it: Topic) =>
                    it.properties.mandatoryForRoles.some(
                      (role) =>
                        !!props.company.members[clone.memberUserId].roles[role]
                    )
                  );
                  clone.properties.topics = mandatorPrograms.map((t) => ({
                    topicId: t.id!,
                    addedByUser: Number(props.thisUser.userId),
                    addedInPhase: props.phase?.id || '',
                    approvedStateByUsers: [
                      {
                        userId: Number(props.thisUser.userId),
                        phaseId: props.phase!.id!,
                        approved: 'Approved',
                        tags: [],
                      },
                    ],
                  }));
                  setEditingProgram(clone);
                }}
                options={userOptions}
                values={
                  program?.memberUserId
                    ? [
                        {
                          value: program?.memberUserId,
                          label: props.users[program?.memberUserId].name || '',
                        },
                      ]
                    : []
                }
              />
            ) : (
              <Collapsible
                style={{
                  width: '100%',
                  margin: 0,
                  borderRadius: '10px 10px 0 0',
                }}
                customHeaderStyle={{ flexGrow: 1 }}
                headerWrapperPadding={'20px 5px 5px'}
                header={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                      color: `${black}`,
                    }}
                  >
                    <span>
                      <b>
                        {props.users[props.program?.memberUserId || 0]?.name}
                      </b>
                      <div className="text-center text-sm">
                        {props.program?.memberUserId &&
                          props.company.members[props.program?.memberUserId]
                            .employeeId}
                      </div>
                    </span>
                    <span>{messages.roles}</span>
                  </div>
                }
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '5px',
                  }}
                >
                  {props.program?.memberUserId &&
                    Object.values(
                      props.company.members[props.program?.memberUserId]
                        ?.roles ?? {}
                    )
                      .filter(
                        (it) => it.id === it.sourceRoleId || !it.sourceRoleId
                      )
                      .map((it) => {
                        return (
                          <Badge color={stringToColour(it.name)} key={it.id}>
                            {it.name}
                          </Badge>
                        );
                      })}
                </div>
              </Collapsible>
            )}
          </div>
        </div>
        {editing && allowAdd && !!editingProgram.id && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <h5 style={{ margin: '10px' }}>{messages.selectedTopics}:</h5>
              <Button
                onClick={() => {
                  setShowAddModal(true);
                }}
                style={{ margin: '1rem' }}
              >
                {' '}
                <Icon type={'plus'} />
                {messages.addTopic}{' '}
              </Button>
            </div>
            <Modal
              isActive={showAddModal}
              title={messages.addTopic}
              onClose={() => setShowAddModal(false)}
            >
              <div style={{ height: '100%' }}>
                {/*<Select
                  disabled={requestingTopic}
                  options={[
                    ...(Object.values(props.pdpState.topics!) as Topic[])
                      .filter((it: Topic) => {
                        if (!program.memberUserId) return false;
                        if (
                          it.status === 'Archived' ||
                          it.status === 'Disabled'
                        )
                          return false;
                        if (
                          it.status === 'Proposed' ||
                          it.properties.relatedToRoles.find((it) => {
                            return !!props.company.members[program.memberUserId]
                              .roles[it];
                          })
                        )
                          return true;
                        return false;
                      })
                      .map((it: Topic) => {
                        return {
                          value: it.id!,
                          label: it.properties.name!,
                          disabled: !!program.properties.topics.find(
                            (f) => f.topicId === it.id
                          ),
                        };
                      }),
                    { value: -1, label: messages.requestNewTopicChoice },
                  ]}
                  letAddChip={true}
                  addPrefix={messages.requestNewTopic}
                  placeholder={messages.addTopic}
                  values={[]}
                  containerStyle={{ margin: '10px' }}
                  onChangeValues={(values) => {
                    if (typeof values[0].value === 'string') {
                      setNewTopicName(values[0].value);
                      setRequestingNewTopicModalOpen(true);
                    } else if (values[0].value === -1) {
                      setNewTopicName('');
                      setRequestingNewTopicModalOpen(true);
                    } else
                      setCandidTopic(values.map((it) => Number(it.value))[0]);
                  }}
                />*/}
                {!toAddTopic && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Input
                      id="searchtopic"
                      type="text"
                      placeholder={messages.searchTopic}
                      value={searchTopicText}
                      onChange={(e) => setSearchTopicText(e.target.value)}
                    ></Input>
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      {(Object.values(props.pdpState.topics!) as Topic[])
                        .filter((it: Topic) => {
                          if (!it.properties.name.includes(searchTopicText))
                            return false;
                          if (!program.memberUserId) return false;
                          if (
                            it.status === 'Archived' ||
                            it.status === 'Disabled'
                          )
                            return false;
                          if (
                            it.status === 'Proposed' ||
                            it.properties.relatedToRoles.find((it) => {
                              return !!props.company.members[
                                program.memberUserId
                              ].roles[it];
                            })
                          )
                            return true;
                          return false;
                        })
                        .map((topic) => {
                          const exist = !!program.properties.topics.find(
                            (f) => f.topicId === topic.id
                          );
                          return (
                            <TopicItem
                              key={topic.id}
                              style={{ color: exist ? gray : black }}
                              onClick={() => {
                                if (!exist) setCandidTopic(topic.id!);
                              }}
                            >
                              {exist && (
                                <Icon
                                  type={'check'}
                                  style={{ marginLeft: '5px' }}
                                />
                              )}
                              {topic.properties.name}
                            </TopicItem>
                          );
                        })}
                      <TopicItem
                        onClick={() => {
                          setRequestingNewTopicModalOpen(true);
                          setNewTopicName(searchTopicText);
                        }}
                      >
                        <Icon type={'plus'} /> <b>{messages.requestNewTopic}</b>
                      </TopicItem>
                    </div>
                  </div>
                )}
                {toAddTopic && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div>
                      <Button onClick={() => setToAddTopic(undefined)}>
                        <Icon type="arrow-right" />
                        <span>{messages.back}</span>
                      </Button>
                    </div>
                    <p>{messages.pleaseSpecifyTopicDetails}</p>
                    {getTopicCard(toAddTopic.topicId, 'ToAdd')}
                    <Button onClick={addCondidTopic}>
                      {messages.addTopic}
                    </Button>
                  </div>
                )}
              </div>
            </Modal>
          </>
        )}
        {
          <AddModal
            onClose={() => setRequestingNewTopicModalOpen(false)}
            active={requestingNewTopicModalOpen}
            value={newTopicName}
            onChange={(e) => {
              setNewTopicName(e.target.value);
            }}
            onAdd={() => {
              requestTopic(newTopicName);
            }}
            loading={requestingTopic}
          />
        }
        <div style={{ margin: '10px', overflow: 'overlay', flexGrow: 1 }}>
          <div style={{ flexGrow: 1, overflow: 'overlay' }}>
            <div
              style={{
                height: '100%',
              }}
            >
              <div
                style={{
                  height: '100%',
                  overflow: 'hidden auto',
                  flexGrow: 1,
                }}
              >
                {!props.simple &&
                  (limits.length > 0 || problems.length > 0) && (
                    <Collapsible
                      style={{ margin: 0 }}
                      header={
                        <span style={{ color: orange }}>
                          <Icon
                            style={{ marginLeft: '10px' }}
                            type={'info'}
                            color={orange}
                          />
                          {messages.considerLimits}
                        </span>
                      }
                      isOpen={limitsOpen}
                      onClose={() => setLimitsOpen(false)}
                      onOpen={() => setLimitsOpen(true)}
                    >
                      <ErrorWrapper>
                        {limits.map((limit, index) => {
                          if (!limit) return <></>;
                          const error = problems.find((it) => {
                            return it.relatedTagId === limit!.tagId;
                          });
                          const relatedError = error?.level;
                          return withTitle(
                            <div
                              key={index}
                              style={{
                                color:
                                  relatedError === 'error'
                                    ? red
                                    : relatedError === 'warning'
                                      ? orange
                                      : green,
                              }}
                            >
                              <Icon
                                style={{ margin: '5px 0 5px 10px' }}
                                type={
                                  relatedError === 'error'
                                    ? 'alert-triangle'
                                    : relatedError === 'warning'
                                      ? 'alert-octagon'
                                      : 'check-square'
                                }
                              />
                              {limit.message}
                              {isANumber(error?.currentCount)
                                ? messages.selected(error!.currentCount!)
                                : ''}
                            </div>,
                            error?.message
                          );
                        })}
                        {problems
                          .filter((it) => it.relatedTagId <= -2)
                          .map((p, index) => {
                            return (
                              <div
                                key={index}
                                style={{
                                  color: p.level === 'error' ? red : orange,
                                }}
                              >
                                <Icon
                                  type={
                                    p.level === 'error'
                                      ? 'alert-triangle'
                                      : 'alert-octagon'
                                  }
                                  style={{ marginLeft: '5px' }}
                                  color={p.level === 'error' ? red : orange}
                                />
                                <>
                                  {p.topics
                                    ?.map(
                                      (item) =>
                                        props.pdpState.topics?.[item].properties
                                          ?.name || ''
                                    )
                                    .reduce(
                                      (
                                        current: string,
                                        item: string,
                                        index,
                                        array
                                      ) => {
                                        let ret = current;
                                        if (ret.length > 0)
                                          ret += messages.comma;
                                        ret += item || '';
                                        if (index === array.length - 1)
                                          ret += ': ';
                                        if (array.length > 1)
                                          ret = messages.topics + ' ' + ret;
                                        else ret = messages.topic + ' ' + ret;
                                        return ret;
                                      },
                                      ''
                                    ) ?? <span></span>}
                                  {p.message}
                                  {(p.toAddTopics?.length ?? 0) === 0
                                    ? ''
                                    : ': ' +
                                      p.toAddTopics?.reduce(
                                        (current: string, item: number) => {
                                          let ret = current;
                                          if (ret.length > 0)
                                            ret += messages.comma;
                                          ret +=
                                            props.pdpState.topics?.[item]
                                              .properties?.name || '';
                                          return ret;
                                        },
                                        ''
                                      )}
                                </>
                                {(p.toAddTopics?.length || 0) > 0 &&
                                  editing &&
                                  allowAdd && (
                                    <Button
                                      onClick={() => {
                                        const toApprove =
                                          [] as PersonalTermTopic[];
                                        const toAdd = [] as number[];
                                        p.toAddTopics?.forEach((t) => {
                                          const exists =
                                            program.properties.topics.find(
                                              (it) => it.topicId === t
                                            );
                                          if (exists) toApprove.push(exists);
                                          else toAdd.push(t);
                                        });
                                        const clone = addTopic(toAdd);
                                        approveTopic(clone, ...toApprove);
                                      }}
                                      color={'blue-outline'}
                                      style={{
                                        height: '25px',
                                        margin: '0 10px',
                                      }}
                                    >
                                      {messages.addAll}
                                    </Button>
                                  )}
                              </div>
                            );
                          })}
                        <Button
                          style={{ marginTop: '10px' }}
                          onClick={() => {
                            setLimitsOpen(false);
                          }}
                        >
                          {messages.gotIt}
                        </Button>
                      </ErrorWrapper>
                    </Collapsible>
                  )}
                <PersonTopicList>
                  {['New', 'Unknown', 'Approved', 'Rejected'].map((state) => {
                    return (
                      <>
                        {Object.entries(currentTopicMap)
                          .filter(
                            ([_, status]) =>
                              status ===
                              state /*&& (oldTopicMap[key] !== 'Rejected' || status === 'Approved')*/
                          )
                          .sort(
                            (a, b) =>
                              topicIndex[Number(b[0])] -
                              topicIndex[Number(a[0])]
                          )
                          .map(([key, status], index) => {
                            return (
                              <>
                                {props.simple && !index && (
                                  <p style={{ gridColumn: '1/-1' }}>
                                    {messages[state]}:
                                  </p>
                                )}
                                {getTopicCard(Number(key), status)}
                              </>
                            );
                          })}
                      </>
                    );
                  })}
                  {Object.keys(currentTopicMap).length === 0 && (
                    <div style={{ margin: '5rem auto' }}>{messages.empty}</div>
                  )}
                </PersonTopicList>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            margin: '10px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '10px',
            borderTop: `1px solid ${lightGray}`,
          }}
        >
          {' '}
          {messages.overview}:
          {['New', 'Unknown', 'Approved', 'Rejected'].map((type) => {
            const count = Object.entries(currentTopicMap).filter(
              ([_, value]) => {
                return value === type;
              }
            ).length;
            if (count > 0)
              return (
                <Badge key={type} color={typeColors[type]} textColor={black}>
                  {messages[type]} : {convertDigitToProperFormat(count)}
                </Badge>
              );
            return <></>;
          })}
        </div>
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            gap: '10px',
            margin: '10px',
            alignItems: 'center',
          }}
        >
          {editing && (
            <>
              {program.properties.updateDate && (
                <span>
                  {messages.lastUpdate}:
                  {smartDateString(program.properties.updateDate)}
                </span>
              )}
              <div style={{ flexGrow: 1 }} />
              {props.isPDPAdmin && props.deletePDPProgram && (
                <Dropdown
                  marginToTarget={0}
                  placement="top-start"
                  width={200}
                  target={
                    <MoreSpan>
                      <Icon type="more-horizontal" />
                    </MoreSpan>
                  }
                >
                  <Menu>
                    <MenuItem>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            confirm(
                              messages.confirmDeletePDP(
                                props.users[program?.memberUserId].name
                              )
                            )
                          ) {
                            setLoading(true);

                            props.deletePDPProgram!(editingProgram!)
                              .then(() => {
                                notify(messages.pdpRemoved);
                                setEditing(false);
                                props.setDirty?.(false);
                                props.close?.(true);
                              })
                              .catch((reason) => {
                                notifyError(reason);
                              })
                              .finally(() => {
                                setLoading(false);
                              });
                          }
                        }}
                      >
                        <Icon
                          style={{ marginLeft: '5px' }}
                          type={'trash'}
                          color={red}
                        />{' '}
                        {messages.delete}
                      </span>
                    </MenuItem>
                  </Menu>
                </Dropdown>
              )}

              {!props.new && props.close && (
                <Button
                  onClick={() => {
                    setEditingProgram(props.program!);
                    setEditing(false);
                    props.setDirty?.(false);
                    props.close?.(true);
                  }}
                  color={'red-outline'}
                >
                  {messages.cancel}
                </Button>
              )}
              <Button
                onClick={() => {
                  setLoading(true);
                  sendAssessmentTrack();
                  props
                    .createOrUpdatePDPProgram(editingProgram!)
                    .then(() => {
                      notify(messages.pdpSaved);
                    })
                    .catch((reason) => {
                      notifyError(reason);
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
                color={'blue'}
                disabled={!program.memberUserId}
                loading={loading}
              >
                {messages.save}
              </Button>
            </>
          )}
        </div>
      </TermEditorWrapper>
    </>
  );
  return ret;
};
